import type { TooltipFormatterContext } from '@/ui/chart/builder/parts/tooltip.utils';
import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { extractValue } from '@/utils/rxjs/helpers/rxjs.helpers';
import { uniqueFilter } from '@/utils/toolkit/array/unique-filter.utils';
import { groupBy } from '@/utils/toolkit/object/group-by.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, NgZone, type OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type * as Highcharts from 'highcharts';
import { BehaviorSubject, combineLatest, defer, NEVER, type Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';
import { sseTurquoiseColor } from '../../../constants/color';
import type {
    BusinessIntelligenceDataPoint,
    BusinessIntelligenceDimensionCode,
    BusinessIntelligenceMeasureCode,
    BusinessIntelligenceMemberCode
} from '../../../interfaces/BusinessIntelligence.interface';
import { isDataPointMeaningfulInChart } from '../../../providers/business-intelligence/business-intelligence.utils';
import { HighchartsStaticProvider } from '../../../providers/highcharts/highcharts.static.provider';
import { HighchartsChartComponent } from '../../highcharts-chart/highcharts-chart';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-representation-component.class';
import { BusinessIntelligenceContinuumType } from '../business-intelligence-representation-data.interface';
import type { BusinessIntelligenceRepresentationComponentConfig } from './../../../interfaces/BusinessIntelligenceRepresentation.interface';

export interface BusinessIntelligenceHistogramComponentConfig extends BusinessIntelligenceRepresentationComponentConfig {
    xAxisDimensionCode: BusinessIntelligenceDimensionCode;
    segmentationDimensionCode: BusinessIntelligenceDimensionCode | null;
    clickDimensionCode: BusinessIntelligenceDimensionCode | null;
    measureCode: BusinessIntelligenceMeasureCode;
    areSeriesFormattedForColor: boolean;
    keepZeros: boolean;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-histogram.scss'],
    selector: 'business-intelligence-histogram',
    templateUrl: 'business-intelligence-histogram.html',
    imports: [HighchartsChartComponent, LoadingAndErrorComponent, AsyncPipe]
})
export class BusinessIntelligenceHistogramComponent
    extends BusinessIntelligenceRepresentationComponent<BusinessIntelligenceHistogramComponentConfig>
    implements OnInit
{
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _ngZone = inject(NgZone);

    @Output() clickEvent: EventEmitter<any> = new EventEmitter();

    noData: boolean = false;

    chartConfig$!: Observable<Highcharts.Options>;

    tooltip$ = new BehaviorSubject<{ tooltip: any; content$: Observable<string> } | null>(null);

    private MAX_READABLE_SERIES = 15;

    ngOnInit() {
        this.tooltip$
            .pipe(
                filter(isNotNullish),
                debounceTime(0),
                switchMap(({ tooltip, content$ }) => content$.pipe(map(content => ({ tooltip, content })))),
                untilDestroyed(this)
            )
            .subscribe(({ tooltip, content }) => {
                tooltip.label.attr({
                    text: content
                });
            });

        const tooltip$ = this.tooltip$;

        this.chartConfig$ = combineLatest([this._representationData$, this._config$]).pipe(
            // replace later with proper error handling for wrong configs
            filter(
                ([representationData, config]) =>
                    representationData && !!Object.keys(representationData.metadata).length && isNotNullish(config)
            ),
            tap(([representationData, config]) => {
                const meaningfulData = representationData.data.filter(isDataPointMeaningfulInChart([config.measureCode], config.keepZeros));
                this.noData = !meaningfulData.length;
            }),
            switchMap(([representationData, config]) => {
                return defer(() => {
                    const meaningfulData = representationData.data.filter(
                        isDataPointMeaningfulInChart([config.measureCode], config.keepZeros)
                    );
                    const xAxisValues = meaningfulData.map(d => d.dimensions[config.xAxisDimensionCode]).filter(uniqueFilter);

                    const xAxisValuesIndexes = xAxisValues
                        .map((category, index) => ({ [category]: index }))
                        .reduce((acc, val) => ({ ...acc, ...val }), {});

                    const xAxisContinuity = representationData.metadata[config.xAxisDimensionCode].continuity;

                    const generateSeriesData = (seriesData: BusinessIntelligenceDataPoint[]) => {
                        return seriesData
                            .slice()
                            .sort(
                                (dp1, dp2) =>
                                    xAxisValuesIndexes[dp1.dimensions[config.xAxisDimensionCode]] -
                                    xAxisValuesIndexes[dp2.dimensions[config.xAxisDimensionCode]]
                            )
                            .map(dp => {
                                return {
                                    x: xAxisContinuity
                                        ? xAxisContinuity.getContinuumValueFromValue$(
                                              xAxisContinuity.continuum === BusinessIntelligenceContinuumType.TIMESTAMP
                                                  ? new Date(dp.dimensions[config.xAxisDimensionCode])
                                                  : dp.dimensions[config.xAxisDimensionCode]
                                          )
                                        : xAxisValuesIndexes[dp.dimensions[config.xAxisDimensionCode]],
                                    y: dp.measures[config.measureCode],
                                    clickValue: dp.dimensions[config.clickDimensionCode!]
                                };
                            });
                    };

                    let series: any[];
                    let areSeriesMeaningful: boolean;

                    const segmentationDimensionCode = config.segmentationDimensionCode;

                    if (isNotNullish(segmentationDimensionCode)) {
                        series = Array.from(
                            groupBy(meaningfulData, dataPoint => dataPoint.dimensions[segmentationDimensionCode]).entries()
                        ).map(([seriesCode, values]) => {
                            return {
                                name: representationData.metadata[segmentationDimensionCode].valuesFormatting.formatAsStandaloneText$(
                                    seriesCode
                                ),
                                color: representationData.metadata[segmentationDimensionCode].valuesFormatting.formatAsColor$(seriesCode),
                                data: generateSeriesData(values as any)
                            };
                        });
                        areSeriesMeaningful = true;
                    } else if (
                        !representationData.metadata[config.xAxisDimensionCode].continuity &&
                        config.areSeriesFormattedForColor &&
                        meaningfulData.length <= this.MAX_READABLE_SERIES
                    ) {
                        series = meaningfulData.map(dataPoint => {
                            return {
                                name: representationData.metadata[config.xAxisDimensionCode].valuesFormatting.formatAsStandaloneText$(
                                    dataPoint.dimensions[config.xAxisDimensionCode]
                                ),
                                color: representationData.metadata[config.xAxisDimensionCode].valuesFormatting.formatAsColor$(
                                    dataPoint.dimensions[config.xAxisDimensionCode]
                                ),
                                data: generateSeriesData([dataPoint])
                            };
                        });
                        areSeriesMeaningful = true;
                    } else {
                        series = [
                            {
                                name: representationData.metadata[config.measureCode].getName$(),
                                color: sseTurquoiseColor,
                                data: generateSeriesData(meaningfulData)
                            }
                        ];
                        areSeriesMeaningful = false;
                    }

                    const getValueToFormatFromXValue$ = (xValue: number) =>
                        xAxisContinuity ? xAxisContinuity.getValueFromContinuumValue$(xValue) : of(xAxisValues[xValue]);

                    return combineLatestDeepObject(
                        this._highchartsStaticProvider.buildConfig(this._highchartsStaticProvider.COLUMN_MIN_SIZE_5, {
                            chart: {
                                type: 'column'
                            },
                            boost: {
                                // if necessary, we could work so that the chart works in boost mode.
                                // See that if performance issues arise
                                seriesThreshold: 5000
                            },
                            plotOptions: {
                                column: {
                                    stacking: 'normal',
                                    shadow: false,
                                    borderWidth: 0,
                                    // if necessary, we could work so that the chart works in boost mode.
                                    // See that if performance issues arise
                                    boostThreshold: 0, // disabled
                                    turboThreshold: 0 // disabled
                                },
                                series: {
                                    events: {
                                        click: (event: any) => {
                                            if (!config.clickDimensionCode) {
                                                return;
                                            }
                                            this._ngZone.run(() => {
                                                this.clickEvent.emit(event.point.clickValue);
                                            });
                                        }
                                    },
                                    cursor: isNotNullish(config.clickDimensionCode) ? 'pointer' : 'default'
                                }
                            },
                            xAxis: {
                                labels: {
                                    formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                                        // TODO : handle async updates
                                        return extractValue(
                                            getValueToFormatFromXValue$(this.value as number).pipe(
                                                switchMap(valueToFormat =>
                                                    representationData.metadata[
                                                        config.xAxisDimensionCode
                                                    ].valuesFormatting.formatAsStandaloneText$(valueToFormat)
                                                )
                                            )
                                        );
                                    },
                                    padding: 12
                                },
                                type:
                                    xAxisContinuity && xAxisContinuity.continuum === BusinessIntelligenceContinuumType.TIMESTAMP
                                        ? 'datetime'
                                        : xAxisContinuity && xAxisContinuity.continuum === BusinessIntelligenceContinuumType.NUMBER
                                          ? 'linear'
                                          : 'category'
                            },
                            yAxis: {
                                title: {
                                    text: super.getMemberName$(config.measureCode, representationData)
                                },
                                labels: {
                                    formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                                        // TODO : handle async updates
                                        // TODO : use grouped text formatting when it will be available
                                        return extractValue(
                                            representationData.metadata[config.measureCode].valuesFormatting.formatAsStandaloneText$(
                                                this.value as number
                                            )
                                        );
                                    }
                                }
                            },
                            tooltip: {
                                formatter(this: TooltipFormatterContext, tooltip: string) {
                                    tooltip$.next({
                                        tooltip,
                                        content$: combineLatest([
                                            representationData.metadata[config.measureCode].valuesFormatting.formatAsStandaloneText$(
                                                this.point.y
                                            ),
                                            getValueToFormatFromXValue$(this.point.x).pipe(
                                                switchMap(valueToFormat =>
                                                    representationData.metadata[
                                                        config.xAxisDimensionCode
                                                    ].valuesFormatting.formatAsStandaloneText$(valueToFormat)
                                                )
                                            )
                                        ]).pipe(map(([pointValue, xValue]) => `${xValue} <br/> ${this.series.name}: <b>${pointValue}</b>`))
                                    });
                                    return;
                                }
                            },
                            legend: {
                                enabled: areSeriesMeaningful
                            },
                            series
                        })
                    );
                }).pipe(
                    catchError(err => {
                        console.error('Error while updating histogram config ::', err);
                        return NEVER;
                    })
                );
            })
        );
    }

    getExportMembers(config: BusinessIntelligenceHistogramComponentConfig): BusinessIntelligenceMemberCode[] {
        return config.segmentationDimensionCode
            ? [config.xAxisDimensionCode, config.segmentationDimensionCode, config.measureCode]
            : [config.xAxisDimensionCode, config.measureCode];
    }
}
